import PopupSectionRow from 'components/popup/PopupSectionRow'
import dayjs, { Dayjs } from 'dayjs'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import DatePickPopup from '../popups/eventInfo/DatePickPopup'
import { useCalendar } from 'components/providers/CalendarProvider'
import LocalStorage from 'services/LocalStorage'
import { LocalStorageKey } from 'types/services/localStorage'
import { IEventDetails } from 'hooks/useEventDetails'
import { Box, ClickAwayListener } from '@mui/material'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import { darken } from '@mui/material/styles'
import { TimeField } from '@mui/x-date-pickers/TimeField'
import { CloseIcon } from 'assets/icons'
import { is24meApp } from 'utils/appType'
import { Controller } from 'react-hook-form'
import SelectItemWithSearchPopup, {
  SelectableItem
} from 'components/popups/SelectItemWithSearchPopup'
import dateToTimeString from 'components/calendar/components'
import { minutesToHumanReadable } from 'components/popups/eventInfo/AddReminderPopup'
import { theme } from 'theme'
import { DocType } from 'types/api/changes'
import { isAMPM } from 'utils/date'
import { getDefaultTimezone } from 'index'
import { isCypress } from 'utils/cypress'
import Logger from 'services/Logger'
import { isMobileDevice } from 'utils/constants'
import { isGuest } from 'utils/user'
import { start } from 'repl'

export enum PickerType {
  START,
  END
}

export interface DatePickerProps {
  pickerType: PickerType
  allDay: boolean
  setStartTime: (date: number) => void
  startTime: number
  setEndTime: (date: number) => void
  endTime: number
  timeZone: string | null | undefined
  setTimeZone: (tz?: string | undefined | null) => void
  duration: number
  eventHook: IEventDetails
  shouldShowTimeDropdown?: boolean
}

let utc = require('dayjs/plugin/utc')
let timezone = require('dayjs/plugin/timezone') // dependent on utc plugin
dayjs.extend(utc)
dayjs.extend(timezone)

export function EventDatePicker(props: DatePickerProps) {
  const pickerType = props.pickerType
  const {
    startTime,
    setStartTime,
    endTime,
    setEndTime,
    allDay,
    timeZone,
    setTimeZone,
    shouldShowTimeDropdown,
    duration,
    eventHook
  } = props

  Logger.blue('EventDatePicker render', {
    startTime,
    endTime,
    allDay,
    timeZone
  })

  const tz =
    eventHook.timeZone && eventHook.timeZone?.length > 0
      ? eventHook.timeZone
      : getDefaultTimezone()

  let zonedStart = dayjs(startTime).tz(tz)
  let zonedEnd = dayjs(endTime).tz(tz)

  if (allDay) {
    zonedStart = dayjs(startTime)
    zonedEnd = dayjs(endTime)
  }

  const [dateState, setDateState] = useState(false)

  const openDate = () => {
    setDateState(true)
  }

  const calendar = useCalendar()

  let dayjsTime = pickerType === PickerType.START ? zonedStart : zonedEnd

  const [time, setTime] = useState<Dayjs | null>(dayjsTime)

  useEffect(() => {
    if (props.eventHook.getValues('someday') === '0') {
      setTime(pickerType === PickerType.START ? zonedStart : zonedEnd)
    }
  }, [startTime, endTime])

  const onAcceptDate = (date: Dayjs | null, time?: boolean) => {
    setDateState(false)
    let newDate = date?.tz(props.eventHook.timeZone) ?? dayjs()

    if (!time) {
      newDate =
        newDate?.set(
          'hour',
          pickerType === PickerType.START ? zonedStart.hour() : zonedEnd.hour()
        ) ?? dayjs()
    }

    if (allDay) {
      newDate = date ?? dayjs()
    }

    if (allDay) {
      // in case of changing time to the all day, we need to take into consideration the timezone
      // because, for example if event starts at 03:00 AM in Auckland, when we will look into
      // this same time in UTC (all day) it will be 13:00 of the previous day, we want to preserve original day
      // in epoch format, that's why we need to add/remove hours from the epoch based on timezone offset from utc
      // to keep the same day

      // Convert to the event's local timezone
      const startLocal = dayjs(date?.valueOf()).tz(getDefaultTimezone())

      // Get the offset in minutes
      const offsetMinutes = startLocal.utcOffset()

      // Adjust the start time to preserve the original day in UTC
      // newDate = startLocal.add(offsetMinutes, 'minute').utc().startOf('day')
    }

    setTime(newDate)

    if (pickerType === PickerType.START) {
      const dayVisible =
        newDate.isAfter(
          dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_FROM)).startOf('day')
        ) &&
        newDate.isBefore(
          dayjs(LocalStorage.get(LocalStorageKey.VISIBLE_TO))
            .subtract(1, 'day')
            .endOf('day')
        )
      if (props.eventHook.isNewEvent) {
        if (
          newDate.isAfter(dayjs(calendar.toISO)) ||
          newDate.isBefore(dayjs(calendar.fromISO))
        ) {
          calendar.onGoToDate(newDate.format('YYYY-MM-DD'))
        }
        calendar.onScrollToTime(newDate)
      }

      setStartTime(newDate.valueOf())
      setTimeout(
        () => {
          if (allDay || !props.eventHook.isNewEvent) return
          try {
            ;(calendar.ref as any)?.current.getApi().scrollToTime(
              dayjs(newDate)
                .tz(timeZone ?? getDefaultTimezone())
                .format('HH:mm:ss')
            )
          } catch (e) {
            //can't go to date
          }
        },
        !dayVisible ? 250 : 1
      )
    } else if (pickerType === PickerType.END) {
      console.log(newDate)
      setEndTime(newDate.valueOf())
    }
  }

  const { t } = useTranslation()

  const [showTimeMinutesPopup, setShowTimeMinutesPopup] = useState(false)

  function buildMinutes() {
    let minutes: SelectableItem[] = []
    for (let i = 1; i < 7; i++) {
      const time = zonedEnd
        .add(0 - duration, 'milliseconds')
        .add(i * 30, 'minute')
      const selectableTime: SelectableItem = {
        title: dateToTimeString(time.toDate(), true),
        subTitle: minutesToHumanReadable(i * 30),
        value: time
      }
      minutes.push(selectableTime)
    }
    return minutes
  }

  // Inside your component:
  const timeoutRef = useRef(null)

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return (
    <ClickAwayListener
      mouseEvent="onMouseDown"
      touchEvent="onTouchStart"
      onClickAway={() => {
        setDateState(false)
        setShowTimeMinutesPopup(false)
      }}
    >
      <div>
        <PopupSectionRow
          icon={
            pickerType === PickerType.START ? (
              <div>{t('starts')}</div>
            ) : (
              <div>{t('ends')}</div>
            )
          }
          title={
            <DatePickPopup
              onAccept={onAcceptDate}
              value={startTime === 0 ? dayjs() : dayjsTime}
              open={dateState}
              setOpen={setDateState}
              eventHook={props.eventHook}
              instanceStart={
                pickerType === PickerType.START
                  ? props.startTime
                  : props.endTime
              }
            >
              <div
                id={
                  pickerType === PickerType.START
                    ? 'start-date-picker'
                    : 'end-date-picker'
                }
                style={{
                  cursor: 'pointer',
                  width: 'fit-content'
                }}
                onClick={openDate}
              >
                {props.eventHook.getValues('someday') === '1' ? (
                  <>{t('clickToSet')}</>
                ) : (
                  <Box
                    onClick={() => {}}
                    display={'flex'}
                    flexWrap={'wrap'}
                    flexDirection={'row'}
                    width={'fit-content'}
                    alignItems={'center'}
                    gap={1}
                  >
                    {`${dayjsTime.format(
                      isMobileDevice() || isGuest() ? 'ddd' : 'dddd'
                    )}, ${dayjsTime.format(
                      isMobileDevice() || isGuest() ? 'L' : 'LL'
                    )}`}
                    {is24meApp() && (
                      <Controller
                        name="Type"
                        control={props.eventHook.control}
                        defaultValue={props.eventHook.type}
                        render={({ field }) => {
                          if (field.value !== DocType.TASK) return <></>

                          return (
                            <CloseIcon
                              onClick={(e) => {
                                e.stopPropagation()
                                props.eventHook.setSomeday('1')
                              }}
                            />
                          )
                        }}
                      />
                    )}
                  </Box>
                )}
              </div>
            </DatePickPopup>
          }
          value={
            allDay ? (
              <></>
            ) : (
              <Controller
                name="someday"
                control={props.eventHook.control}
                defaultValue={props.eventHook.someday}
                render={({ field }) => {
                  if (field.value === '1') return <></>

                  return (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <SelectItemWithSearchPopup
                        data={buildMinutes()}
                        width="fit-content"
                        placement="bottom"
                        open={showTimeMinutesPopup}
                        selectedItemId={undefined}
                        rowBuilder={(item) => {
                          return buildTimeRow({
                            item,
                            setShowTimeMinutesPopup,
                            onAcceptDate
                          })
                        }}
                      >
                        <TimeField
                          id={
                            pickerType === PickerType.START
                              ? 'start-time-picker'
                              : 'end-time-picker'
                          }
                          hiddenLabel
                          ampm={isCypress() ? false : isAMPM()}
                          timezone={
                            dayjs().tz(tz).utcOffset() === 0 ? 'UTC' : tz
                          }
                          value={dayjsTime}
                          variant="standard"
                          inputProps={{
                            style: {
                              cursor: 'pointer',
                              fontSize: '14px',
                              padding: 0,
                              borderBottom: 'none',
                              textAlign: 'end',
                              width: '60px'
                            }
                          }}
                          onFocus={
                            shouldShowTimeDropdown
                              ? () => {
                                  setShowTimeMinutesPopup(true)
                                }
                              : undefined
                          }
                          onBlur={() => {}}
                          InputProps={{ disableUnderline: true }}
                          onChange={(newValue) => {
                            setShowTimeMinutesPopup(false)

                            if (timeoutRef.current) {
                              clearTimeout(timeoutRef.current)
                            }

                            ;(timeoutRef.current as any) = setTimeout(() => {
                              setTime(newValue)
                              onAcceptDate(newValue, true)
                            }, 500)
                          }}
                        />
                      </SelectItemWithSearchPopup>
                    </LocalizationProvider>
                  )
                }}
              />
            )
          }
        />
      </div>
    </ClickAwayListener>
  )
}

interface TimeRowProps {
  item: SelectableItem
  setShowTimeMinutesPopup: (show: boolean) => void
  onAcceptDate: (date: Dayjs | null, time?: boolean) => void
}

function buildTimeRow(props: TimeRowProps) {
  const { item, setShowTimeMinutesPopup, onAcceptDate } = { ...props }
  return (
    <Box
      key={item.value.valueOf()}
      onClick={(e) => {
        e.stopPropagation()
        setShowTimeMinutesPopup(false)
        onAcceptDate(item.value, true)
      }}
      width={'fit-content'}
      display={'flex'}
      alignContent={'center'}
      flexDirection={'row'}
      color={'black'}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          backgroundColor: darken(
            theme.palette.button.background.primary as string,
            0.02
          )
        }
      }}
      gap={4}
    >
      <div style={{ minWidth: '65px' }}>{item.title}</div>
      <div style={{ color: theme.palette.text.secondary }}>{item.subTitle}</div>
    </Box>
  )
}
